/**
 * QMS Phase 1: Standard detail – linked requirements, implementation status, protocol assignment (PRD Protocol–Requirement linking).
 * Shows standard (process) and standard_requirements with policy source and assigned protocols; compliance from protocol_compliance_events (Option B).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useStandardRequirements,
  useQmsStandardMutations,
} from '../../hooks/useQmsStandards';
import { usePolicyDocuments, useRequirementsByPolicy } from '../../hooks/useQmsPolicy';
import { useProcessMutations } from '../../hooks/useProcesses';
import qmsStandardService from '../../services/qmsStandardService';
import qmsPolicyService from '../../services/qmsPolicyService';
import { protocolService } from '../../services/protocolService';
import { complianceEventService } from '../../services/complianceEventService';
import { deviationService } from '../../services/deviationService';
import { Trash2, Gauge } from 'lucide-react';
import Modal from '../ui/Modal';
import { useNotifications } from '../NotificationSystem';
import './StandardDetail.css';

const STATUS_OPTIONS = ['Not Started', 'In Progress', 'Implemented', 'At Risk', 'Verified'];
const COMPLIANCE_THRESHOLD = 0.8; // 80% for "Implemented" vs "At Risk"

const DEVIATION_DEFAULTS = {
  timing: { varianceTriggerPct: 50, varianceMajorPct: 80, complianceImpactMinor: 5, complianceImpactMajor: 10 },
  sequence: { severity: 'major', complianceImpact: 15 },
  quality: { severity: 'minor', complianceImpact: 5 },
  risk: { rpnThreshold: 500, severity: 'critical', complianceImpact: 20, rpnImpact: 50 },
};

const StandardDetail = ({ standardId, standard, onBack, organizationId, userId, userName, onStandardUpdated }) => {
  const { t } = useTranslation('qms');
  const { notify } = useNotifications();
  const { links, loading: linksLoading, refresh: refreshLinks } = useStandardRequirements(standardId);
  const mutations = useQmsStandardMutations();
  const { updateProcess } = useProcessMutations();
  const { documents: policyDocuments } = usePolicyDocuments({ status: 'active' });

  const [requirementMap, setRequirementMap] = useState({});
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkModalPolicyDocId, setLinkModalPolicyDocId] = useState('');
  const [removingSrId, setRemovingSrId] = useState(null);
  const [showDeviationThresholds, setShowDeviationThresholds] = useState(false);
  const [deviationThresholds, setDeviationThresholds] = useState({ ...DEVIATION_DEFAULTS });
  const [savingThresholds, setSavingThresholds] = useState(false);
  const [showLinkThresholdsModalForSrId, setShowLinkThresholdsModalForSrId] = useState(null);
  const [linkThresholdsForm, setLinkThresholdsForm] = useState({ ...DEVIATION_DEFAULTS });
  const [savingLinkThresholds, setSavingLinkThresholds] = useState(false);
  // Assign protocol modal (PRD 4.1)
  const [showAssignProtocolForSrId, setShowAssignProtocolForSrId] = useState(null);
  const [assignProtocolList, setAssignProtocolList] = useState([]);
  const [assignProtocolSearch, setAssignProtocolSearch] = useState('');
  const [assignProtocolSelections, setAssignProtocolSelections] = useState([]);
  const [savingAssignProtocol, setSavingAssignProtocol] = useState(false);
  const [removingProtocolIdFromSrId, setRemovingProtocolIdFromSrId] = useState(null);
  // Protocol stats (30-day compliance, last execution, open deviations) per protocolId
  const [protocolStats, setProtocolStats] = useState({});

  useEffect(() => {
    if (standard?.deviationThresholds && typeof standard.deviationThresholds === 'object') {
      setDeviationThresholds({ ...DEVIATION_DEFAULTS, ...standard.deviationThresholds });
    } else {
      setDeviationThresholds({ ...DEVIATION_DEFAULTS });
    }
  }, [standard?.id, standard?.deviationThresholds]);

  const { requirements: linkModalRequirements, loading: linkModalReqsLoading } =
    useRequirementsByPolicy(linkModalPolicyDocId);
  const linkedRequirementIds = links.map((l) => l.requirementId);

  const loadRequirements = useCallback(async () => {
    if (!links.length) {
      setRequirementMap({});
      return;
    }
    const map = {};
    await Promise.all(
      links.map(async (link) => {
        try {
          const req = await qmsPolicyService.getRequirement(link.requirementId);
          map[link.requirementId] = req;
        } catch {
          map[link.requirementId] = null;
        }
      })
    );
    setRequirementMap(map);
  }, [links]);

  useEffect(() => {
    loadRequirements();
  }, [loadRequirements]);

  // Load 30-day compliance and open deviations for all assigned protocols (Option B)
  const loadProtocolStats = useCallback(async () => {
    const protocolIds = new Set();
    links.forEach((link) => {
      (link.assignedProtocols || []).forEach((a) => protocolIds.add(a.protocolId));
    });
    if (protocolIds.size === 0) {
      setProtocolStats({});
      return;
    }
    const map = {};
    await Promise.all(
      [...protocolIds].map(async (pid) => {
        const [rateRes, openCount] = await Promise.all([
          complianceEventService.getProtocol30DayComplianceRate(pid),
          deviationService.getOpenDeviationsCountByProtocol(pid),
        ]);
        map[pid] = {
          rate: rateRes.rate,
          totalEvents: rateRes.totalEvents,
          lastExecution: rateRes.lastExecution,
          openDeviations: openCount,
        };
      })
    );
    setProtocolStats(map);
  }, [links]);

  useEffect(() => {
    loadProtocolStats();
  }, [loadProtocolStats]);

  const handleStatusChange = async (srId, implementationStatus) => {
    try {
      await mutations.updateStandardRequirement(srId, { implementationStatus }, userId, userName);
      notify.success(t('save'));
      refreshLinks();
    } catch (err) {
      notify.error(err.message || t('saveFailed'));
    }
  };

  const handleLinkRequirement = async (requirementId) => {
    if (!requirementId) return;
    try {
      await mutations.createStandardRequirement(
        { standardId, requirementId, implementationStatus: 'Not Started' },
        userId,
        userName
      );
      notify.success(t('requirementLinked'));
      setShowLinkModal(false);
      setLinkModalPolicyDocId('');
      refreshLinks();
    } catch (err) {
      notify.error(err.message || t('saveFailed'));
    }
  };

  const handleRemoveLink = async (srId) => {
    setRemovingSrId(srId);
    try {
      await mutations.deleteStandardRequirement(srId, userId, userName);
      notify.success(t('linkRemoved'));
      refreshLinks();
    } catch (err) {
      notify.error(err.message || t('saveFailed'));
    } finally {
      setRemovingSrId(null);
    }
  };

  // Load protocols when Assign protocol modal opens (Draft, Under Review, Active – exclude Deprecated/Archived)
  useEffect(() => {
    if (!showAssignProtocolForSrId) return;
    let cancelled = false;
    protocolService
      .getAllProtocols({ includeInactive: true })
      .then((list) => {
        if (cancelled) return;
        const assignable = (list || []).filter(
          (p) =>
            (p.is_active !== false && p.isActive !== false) &&
            !['Deprecated', 'Archived'].includes(p.status || '')
        );
        setAssignProtocolList(
          assignable.map((p) => ({ ...p, id: p.id || p.firestoreId }))
        );
      })
      .catch((err) => {
        console.error('Load protocols for assign modal:', err);
        if (!cancelled) setAssignProtocolList([]);
      });
    return () => { cancelled = true; };
  }, [showAssignProtocolForSrId]);

  const handleAssignProtocol = async () => {
    const srId = showAssignProtocolForSrId;
    if (!srId || !assignProtocolSelections.length || !userId) return;
    const link = links.find((l) => l.id === srId);
    const req = link ? requirementMap[link.requirementId] : null;
    const requirementCode = req?.code || '';
    const standardCode = standard?.processCode || standard?.processName || '';
    setSavingAssignProtocol(true);
    try {
      for (const sel of assignProtocolSelections) {
        await qmsStandardService.assignProtocolToRequirement(
          srId,
          {
            protocolId: sel.protocolId,
            protocolName: sel.protocolName,
            protocolCode: sel.protocolCode || (sel.protocolName && sel.protocolName.includes(' - ') ? sel.protocolName.split(' - ')[0].trim() : ''),
            role: sel.role,
            requirementCode,
            standardCode,
          },
          userId,
          userName
        );
      }
      notify.success(t('save'));
      setShowAssignProtocolForSrId(null);
      setAssignProtocolSelections([]);
      refreshLinks();
      loadProtocolStats();
    } catch (err) {
      notify.error(err.message || t('saveFailed'));
    } finally {
      setSavingAssignProtocol(false);
    }
  };

  const handleRemoveProtocolFromRequirement = async (srId, protocolId) => {
    setRemovingProtocolIdFromSrId(`${srId}-${protocolId}`);
    try {
      await qmsStandardService.removeProtocolFromRequirement(srId, protocolId, userId, userName);
      notify.success(t('save'));
      refreshLinks();
      loadProtocolStats();
    } catch (err) {
      notify.error(err.message || t('saveFailed'));
    } finally {
      setRemovingProtocolIdFromSrId(null);
    }
  };

  const handleSaveDeviationThresholds = async () => {
    if (!standardId || !userId) return;
    setSavingThresholds(true);
    try {
      await updateProcess(standardId, { deviationThresholds }, userId, userName);
      notify.success(t('save'));
      const updated = { ...standard, deviationThresholds };
      onStandardUpdated?.(updated);
    } catch (err) {
      notify.error(err.message || t('saveFailed'));
    } finally {
      setSavingThresholds(false);
    }
  };

  const updateThreshold = (category, key, value) => {
    setDeviationThresholds((prev) => ({
      ...prev,
      [category]: { ...(prev[category] || {}), [key]: value },
    }));
  };

  const openLinkThresholdsModal = (link) => {
    const existing = link?.deviationThresholds && typeof link.deviationThresholds === 'object'
      ? { ...DEVIATION_DEFAULTS, ...link.deviationThresholds }
      : { ...DEVIATION_DEFAULTS };
    setLinkThresholdsForm(existing);
    setShowLinkThresholdsModalForSrId(link?.id);
  };

  const updateLinkThreshold = (category, key, value) => {
    setLinkThresholdsForm((prev) => ({
      ...prev,
      [category]: { ...(prev[category] || {}), [key]: value },
    }));
  };

  const handleSaveLinkThresholds = async () => {
    if (!showLinkThresholdsModalForSrId || !userId) return;
    setSavingLinkThresholds(true);
    try {
      await mutations.updateStandardRequirement(
        showLinkThresholdsModalForSrId,
        { deviationThresholds: linkThresholdsForm },
        userId,
        userName
      );
      notify.success(t('save'));
      setShowLinkThresholdsModalForSrId(null);
      refreshLinks();
    } catch (err) {
      notify.error(err.message || t('saveFailed'));
    } finally {
      setSavingLinkThresholds(false);
    }
  };

  const policyDocTitle = (policyDocumentId) => {
    const doc = policyDocuments.find((d) => d.id === policyDocumentId);
    return doc ? doc.title || doc.code : policyDocumentId || '—';
  };

  const policyDocCode = (policyDocumentId) => {
    const doc = policyDocuments.find((d) => d.id === policyDocumentId);
    return doc ? doc.code : null;
  };

  /** Computed implementation status when ≥1 protocol assigned (PRD 4.3). */
  const getComputedImplementationStatus = (link) => {
    const assigned = link.assignedProtocols || [];
    if (assigned.length === 0) return null;
    const primary = assigned.find((a) => a.role === 'primary');
    const stats = primary ? protocolStats[primary.protocolId] : null;
    const rate = stats?.rate ?? 0;
    const hasExecution = (stats?.totalEvents ?? 0) > 0;
    if (rate >= COMPLIANCE_THRESHOLD) return 'Implemented';
    if (hasExecution || rate > 0) return 'At Risk';
    return 'In Progress';
  };

  const filteredAssignProtocolList = assignProtocolSearch.trim()
    ? assignProtocolList.filter(
        (p) =>
          (p.name || '').toLowerCase().includes(assignProtocolSearch.toLowerCase()) ||
          (p.category || '').toLowerCase().includes(assignProtocolSearch.toLowerCase())
      )
    : assignProtocolList;

  const assignModalLink = showAssignProtocolForSrId ? links.find((l) => l.id === showAssignProtocolForSrId) : null;
  const assignModalHasPrimaryAlready = (assignModalLink?.assignedProtocols || []).some((a) => a.role === 'primary');

  const availableRequirements = (linkModalRequirements || []).filter(
    (r) => !linkedRequirementIds.includes(r.id)
  );

  if (!standard && standardId) {
    return (
      <div className="standard-detail">
        <div className="loading-state">
          <div className="spinner" />
          <p>{t('loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="standard-detail">
      <div className="standard-detail-header">
        <button type="button" className="btn btn-secondary btn-back" onClick={onBack}>
          ← {t('backToList')}
        </button>
        <h2 className="standard-detail-title">
          {standard?.processCode || standard?.processName || standardId}
          {standard?.processName && standard?.processCode && ` — ${standard.processName}`}
        </h2>
      </div>
      {standard?.policyDocumentId && (
        <p className="standard-policy-framework">
          {t('fromPolicyDocument')}: {policyDocCode(standard.policyDocumentId) || policyDocTitle(standard.policyDocumentId)}
        </p>
      )}

      <div className="standard-requirements-section">
        <div className="section-header">
          <h3>{t('standardRequirements')}</h3>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              setShowLinkModal(true);
              setLinkModalPolicyDocId(policyDocuments[0]?.id || '');
            }}
          >
            {t('addRequirementLink')}
          </button>
        </div>

        {!linksLoading && links.length > 0 && (
          <div className="compliance-rollup-header">
            <span>{t('totalRequirements')}: <strong>{links.length}</strong></span>
            <span>{t('requirementsWithProtocols')}: <strong>{links.filter((l) => (l.assignedProtocols || []).length > 0).length}</strong></span>
            <span>{t('atOrAboveThreshold')}: <strong>{links.filter((l) => {
              const comp = getComputedImplementationStatus(l);
              return comp === 'Implemented';
            }).length}</strong></span>
            <span>{t('belowOrOpenDeviations')}: <strong>{links.filter((l) => {
              const comp = getComputedImplementationStatus(l);
              const assigned = l.assignedProtocols || [];
              if (assigned.length === 0) return false;
              return comp === 'At Risk' || (protocolStats[assigned[0]?.protocolId]?.openDeviations ?? 0) > 0;
            }).length}</strong></span>
            <span>{t('overallImplementation')}: <strong>{links.length ? Math.round((links.filter((l) => (l.assignedProtocols || []).length > 0).length / links.length) * 100) : 0}%</strong></span>
          </div>
        )}
        {linksLoading ? (
          <div className="loading-state">
            <div className="spinner" />
            <p>{t('loading')}</p>
          </div>
        ) : links.length === 0 ? (
          <p className="empty-message">
            {t('noRequirements')}
            {standard?.policyDocumentId && (
              <span className="empty-message-hint"> — {t('noRequirementsHint')}</span>
            )}
          </p>
        ) : (
          <div className="requirements-table-wrap">
            <table className="standard-requirements-table">
              <colgroup>
                <col style={{ width: '10%' }} />
                <col style={{ width: '30%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '14%' }} />
                <col style={{ width: '22%' }} />
                <col style={{ width: '12%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th>{t('code')}</th>
                  <th>{t('requirementText')}</th>
                  <th>{t('policySource')}</th>
                  <th>{t('implementationStatus')}</th>
                  <th>{t('protocols')}</th>
                  <th>{t('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {links.map((link) => {
                  const req = requirementMap[link.requirementId];
                  const policyTitle = req?.policyDocumentId
                    ? policyDocTitle(req.policyDocumentId)
                    : '—';
                  const assigned = link.assignedProtocols || [];
                  const computedStatus = getComputedImplementationStatus(link);
                  const isStatusReadOnly = assigned.length > 0;
                  const displayStatus = isStatusReadOnly ? (computedStatus || 'In Progress') : (link.implementationStatus || 'Not Started');
                  const requirementDescription =
                    req && (req.requirementText !== undefined && req.requirementText !== null
                      ? String(req.requirementText).trim()
                      : '');
                  const requirementUnavailable = req === null;
                  return (
                    <tr key={link.id}>
                      <td>{req?.code ?? (requirementUnavailable ? link.requirementId : '—')}</td>
                      <td className="requirement-description-cell" title={requirementDescription || undefined}>
                        {req === undefined ? (
                          <span className="muted">…</span>
                        ) : requirementUnavailable ? (
                          <span className="muted">{t('requirementUnavailable') || 'Cerință indisponibilă'}</span>
                        ) : requirementDescription ? (
                          requirementDescription
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td className="policy-source-cell" title={policyTitle}>
                        {policyTitle}
                      </td>
                      <td>
                        {isStatusReadOnly ? (
                          <span className="status-readonly" title={t('computedFromProtocols')}>
                            {t(displayStatus === 'At Risk' ? 'atRisk' : displayStatus === 'Not Started' ? 'notStarted' : displayStatus === 'In Progress' ? 'inProgress' : displayStatus === 'Implemented' ? 'implemented' : 'verified')}
                          </span>
                        ) : (
                          <select
                            value={displayStatus}
                            onChange={(e) =>
                              handleStatusChange(link.id, e.target.value)
                            }
                            className="status-select"
                          >
                            {STATUS_OPTIONS.map((s) => (
                              <option key={s} value={s}>
                                {t(s === 'Not Started' ? 'notStarted' : s === 'In Progress' ? 'inProgress' : s === 'Implemented' ? 'implemented' : s === 'At Risk' ? 'atRisk' : 'verified')}
                              </option>
                            ))}
                          </select>
                        )}
                      </td>
                      <td>
                        <div className="assigned-protocols-cell">
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={() => {
                              setShowAssignProtocolForSrId(link.id);
                              setAssignProtocolSelections([]);
                              setAssignProtocolSearch('');
                            }}
                          >
                            {t('assignProtocol')}
                          </button>
                          {assigned.length === 0 ? (
                            <p className="assigned-protocols-empty">{t('noProtocolsAssigned')}</p>
                          ) : (
                            <ul className="assigned-protocols-list">
                              {assigned.map((a) => {
                                const stats = protocolStats[a.protocolId] || {};
                                const lastExec = stats.lastExecution?.toDate?.();
                                const displayCode = a.protocolCode || (a.protocolName && a.protocolName.includes(' - ') ? a.protocolName.split(' - ')[0].trim() : (a.protocolName?.slice(0, 14) || '') + (a.protocolName?.length > 14 ? '…' : ''));
                                const openCount = stats.openDeviations ?? 0;
                                return (
                                  <li key={a.protocolId} className="assigned-protocol-item">
                                    <span className="protocol-code-only" title={a.protocolName || undefined}>{displayCode || '—'}</span>
                                    <span className={`role-badge role-${a.role}`}>{a.role === 'primary' ? t('primary') : t('supporting')}</span>
                                    <span
                                      className="compliance-rate"
                                      title={`Rata de conformitate (ultimele 30 zile): ${Math.round((stats.rate ?? 0) * 100)}% din execuții cu scor ≥ 80%${stats.totalEvents ? ` (${stats.totalEvents} execuții)` : ' — nicio execuție înregistrată'}`}
                                    >{Math.round((stats.rate ?? 0) * 100)}%</span>
                                    <span
                                      className="last-execution"
                                      title={lastExec ? `Ultima execuție verificată: ${lastExec.toLocaleDateString()}` : 'Protocolul nu a fost executat încă'}
                                    >{lastExec ? lastExec.toLocaleDateString() : '—'}</span>
                                    <span
                                      className="open-deviations"
                                      title={openCount > 0 ? `${openCount} deviere${openCount > 1 ? 'i' : ''} deschisă${openCount > 1 ? '' : ''} — probleme înregistrate nerezolvate față de acest protocol` : 'Nicio deviere deschisă față de acest protocol'}
                                    >{openCount}</span>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-delete btn-icon-only"
                                      disabled={removingProtocolIdFromSrId === `${link.id}-${a.protocolId}`}
                                      onClick={() => handleRemoveProtocolFromRequirement(link.id, a.protocolId)}
                                      title={t('removeProtocolAssignment')}
                                      aria-label={t('remove')}
                                    >
                                      <Trash2 size={14} />
                                    </button>
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="requirement-actions">
                          <button
                            type="button"
                            className="btn btn-sm btn-edit btn-icon-only"
                            onClick={() => openLinkThresholdsModal(link)}
                            title={t('deviationThresholds')}
                            aria-label={t('thresholds')}
                          >
                            <Gauge size={18} strokeWidth={2} />
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-delete btn-icon-only"
                            disabled={removingSrId === link.id}
                            onClick={() => handleRemoveLink(link.id)}
                            title={t('removeLink')}
                            aria-label={t('removeLink')}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="standard-deviation-thresholds-section">
        <button
          type="button"
          className="section-toggle"
          onClick={() => setShowDeviationThresholds(!showDeviationThresholds)}
        >
          {showDeviationThresholds ? '▼' : '▶'} {t('deviationThresholds')}
        </button>
        {showDeviationThresholds && (
          <div className="deviation-thresholds-panel">
            <p className="deviation-thresholds-hint">
              {t('deviationThresholdsHint')}
            </p>
            <div className="deviation-thresholds-grid">
              <div className="threshold-group">
                <h4>{t('timing')}</h4>
                <div className="threshold-row">
                  <label>Variance trigger %</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={deviationThresholds.timing?.varianceTriggerPct ?? 50}
                    onChange={(e) => updateThreshold('timing', 'varianceTriggerPct', parseInt(e.target.value, 10) || 50)}
                  />
                </div>
                <div className="threshold-row">
                  <label>Variance major %</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={deviationThresholds.timing?.varianceMajorPct ?? 80}
                    onChange={(e) => updateThreshold('timing', 'varianceMajorPct', parseInt(e.target.value, 10) || 80)}
                  />
                </div>
              </div>
              <div className="threshold-group">
                <h4>{t('sequence')}</h4>
                <div className="threshold-row">
                  <label>Severity</label>
                  <select
                    value={deviationThresholds.sequence?.severity ?? 'major'}
                    onChange={(e) => updateThreshold('sequence', 'severity', e.target.value)}
                  >
                    <option value="minor">{t('nomenclatures:options.minor')}</option>
                    <option value="major">{t('nomenclatures:options.major')}</option>
                    <option value="critical">{t('nomenclatures:options.critical')}</option>
                  </select>
                </div>
                <div className="threshold-row">
                  <label>Compliance impact</label>
                  <input
                    type="number"
                    min="0"
                    value={deviationThresholds.sequence?.complianceImpact ?? 15}
                    onChange={(e) => updateThreshold('sequence', 'complianceImpact', parseInt(e.target.value, 10) || 15)}
                  />
                </div>
              </div>
              <div className="threshold-group">
                <h4>{t('quality')}</h4>
                <div className="threshold-row">
                  <label>Severity</label>
                  <select
                    value={deviationThresholds.quality?.severity ?? 'minor'}
                    onChange={(e) => updateThreshold('quality', 'severity', e.target.value)}
                  >
                    <option value="minor">{t('nomenclatures:options.minor')}</option>
                    <option value="major">{t('nomenclatures:options.major')}</option>
                    <option value="critical">{t('nomenclatures:options.critical')}</option>
                  </select>
                </div>
                <div className="threshold-row">
                  <label>Compliance impact</label>
                  <input
                    type="number"
                    min="0"
                    value={deviationThresholds.quality?.complianceImpact ?? 5}
                    onChange={(e) => updateThreshold('quality', 'complianceImpact', parseInt(e.target.value, 10) || 5)}
                  />
                </div>
              </div>
              <div className="threshold-group">
                <h4>{t('risk')}</h4>
                <div className="threshold-row">
                  <label>RPN threshold</label>
                  <input
                    type="number"
                    min="0"
                    value={deviationThresholds.risk?.rpnThreshold ?? 500}
                    onChange={(e) => updateThreshold('risk', 'rpnThreshold', parseInt(e.target.value, 10) || 500)}
                  />
                </div>
                <div className="threshold-row">
                  <label>Severity</label>
                  <select
                    value={deviationThresholds.risk?.severity ?? 'critical'}
                    onChange={(e) => updateThreshold('risk', 'severity', e.target.value)}
                  >
                    <option value="minor">{t('nomenclatures:options.minor')}</option>
                    <option value="major">{t('nomenclatures:options.major')}</option>
                    <option value="critical">{t('nomenclatures:options.critical')}</option>
                  </select>
                </div>
                <div className="threshold-row">
                  <label>Compliance impact</label>
                  <input
                    type="number"
                    min="0"
                    value={deviationThresholds.risk?.complianceImpact ?? 20}
                    onChange={(e) => updateThreshold('risk', 'complianceImpact', parseInt(e.target.value, 10) || 20)}
                  />
                </div>
              </div>
            </div>
            <div className="form-actions">
              <button
                type="button"
                className="btn btn-primary"
                disabled={savingThresholds}
                onClick={handleSaveDeviationThresholds}
              >
                {savingThresholds ? t('saving') : t('save')}
              </button>
            </div>
          </div>
        )}
      </div>

      <Modal
        isOpen={!!showAssignProtocolForSrId}
        onClose={() => {
          setShowAssignProtocolForSrId(null);
          setAssignProtocolSelections([]);
          setAssignProtocolSearch('');
        }}
        title={t('assignProtocol')}
        size="large"
      >
        <div className="assign-protocol-modal">
          <p className="assign-protocol-helper">
            {t('assignProtocolPrimaryHelper')}
          </p>
          <div className="form-group">
            <label>{t('searchProtocols')}</label>
            <input
              type="text"
              className="form-control"
              value={assignProtocolSearch}
              onChange={(e) => setAssignProtocolSearch(e.target.value)}
              placeholder={t('searchByNameOrCategory')}
            />
          </div>
          <div className="assign-protocol-two-col">
            <div>
              <label>{t('activeProtocols')}</label>
              <ul className="protocol-pick-list">
                {filteredAssignProtocolList.length === 0 ? (
                  <li className="protocol-pick-empty">
                    {assignProtocolList.length === 0
                      ? t('noProtocolsAvailable')
                      : t('noMatchingProtocols')}
                  </li>
                ) : filteredAssignProtocolList.map((p) => {
                  const id = p.id || p.firestoreId;
                  const alreadySelected = assignProtocolSelections.some((s) => s.protocolId === id);
                  const hasPrimaryInSelections = assignProtocolSelections.some((s) => s.role === 'primary');
                  const primaryDisabled = assignModalHasPrimaryAlready || hasPrimaryInSelections;
                  return (
                    <li key={id} className="protocol-pick-item">
                      <span className="protocol-pick-name">{p.name}</span>
                      {p.category && <span className="protocol-pick-cat">{p.category}</span>}
                      {!alreadySelected && (
                        <>
                          <button type="button" className="btn btn-sm btn-primary" onClick={() => {
                            if (assignProtocolSelections.some((s) => s.protocolId === id)) return;
                            setAssignProtocolSelections((prev) => [...prev, { protocolId: id, protocolName: p.name, protocolCode: p.protocol_code || (p.name && p.name.includes(' - ') ? p.name.split(' - ')[0].trim() : ''), role: 'supporting' }]);
                          }}>
                            + {t('supporting')}
                          </button>
                          <button type="button" className="btn btn-sm btn-secondary" disabled={primaryDisabled} title={assignModalHasPrimaryAlready ? t('primaryAlreadyAssigned') : undefined} onClick={() => {
                            if (assignProtocolSelections.some((s) => s.protocolId === id)) return;
                            if (primaryDisabled) return;
                            setAssignProtocolSelections((prev) => [...prev, { protocolId: id, protocolName: p.name, protocolCode: p.protocol_code || (p.name && p.name.includes(' - ') ? p.name.split(' - ')[0].trim() : ''), role: 'primary' }]);
                          }}>
                            + {t('primary')}
                          </button>
                        </>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
            <div>
              <label>{t('selectedForAssignment')}</label>
              <ul className="protocol-selections-list">
                {assignProtocolSelections.map((s) => (
                  <li key={s.protocolId}>
                    {s.protocolName} — <span className={`role-badge role-${s.role}`}>{s.role}</span>
                    <button type="button" className="btn btn-sm btn-delete btn-icon-only" onClick={() => setAssignProtocolSelections((prev) => prev.filter((p) => p.protocolId !== s.protocolId))} title={t('remove')} aria-label={t('remove')}><Trash2 size={14} /></button>
                  </li>
                ))}
              </ul>
              {assignProtocolSelections.filter((s) => s.role === 'primary').length > 1 && (
                <p className="validation-error">{t('onlyOnePrimary')}</p>
              )}
            </div>
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => { setShowAssignProtocolForSrId(null); setAssignProtocolSelections([]); }}>{t('cancel')}</button>
            <button type="button" className="btn btn-primary" disabled={savingAssignProtocol || assignProtocolSelections.length === 0 || assignProtocolSelections.filter((s) => s.role === 'primary').length > 1} onClick={handleAssignProtocol}>
              {savingAssignProtocol ? t('saving') : t('confirmAssign')}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showLinkModal}
        onClose={() => {
          setShowLinkModal(false);
          setLinkModalPolicyDocId('');
        }}
        title={t('linkRequirement')}
        size="medium"
      >
        <div className="link-requirement-modal">
          <div className="form-group">
            <label>{t('selectPolicyDocument')}</label>
            <select
              value={linkModalPolicyDocId}
              onChange={(e) => setLinkModalPolicyDocId(e.target.value)}
            >
              <option value="">—</option>
              {policyDocuments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.code} — {d.title}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>{t('selectRequirement')}</label>
            {linkModalReqsLoading ? (
              <p>{t('loading')}</p>
            ) : (
              <select
                onChange={(e) => {
                  const id = e.target.value;
                  if (id) handleLinkRequirement(id);
                }}
                value=""
              >
                <option value="">—</option>
                {availableRequirements.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.code} — {((r.requirementText || '').length > 60 ? `${(r.requirementText || '').slice(0, 60)}…` : (r.requirementText || ''))}
                  </option>
                ))}
              </select>
            )}
            {linkModalPolicyDocId && availableRequirements.length === 0 && !linkModalReqsLoading && (
              <p className="hint">{t('noRequirements')}</p>
            )}
          </div>
          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setShowLinkModal(false);
                setLinkModalPolicyDocId('');
              }}
            >
              {t('cancel')}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={!!showLinkThresholdsModalForSrId}
        onClose={() => setShowLinkThresholdsModalForSrId(null)}
        title={t('requirementDeviationThresholds')}
        size="medium"
      >
        <div className="link-thresholds-modal">
          <p className="deviation-thresholds-hint">
            {t('requirementThresholdsHint')}
          </p>
          <div className="deviation-thresholds-grid">
            <div className="threshold-group">
              <h4>{t('timing')}</h4>
              <div className="threshold-row">
                <label>Variance trigger %</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={linkThresholdsForm.timing?.varianceTriggerPct ?? 50}
                  onChange={(e) => updateLinkThreshold('timing', 'varianceTriggerPct', parseInt(e.target.value, 10) || 50)}
                />
              </div>
              <div className="threshold-row">
                <label>Variance major %</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={linkThresholdsForm.timing?.varianceMajorPct ?? 80}
                  onChange={(e) => updateLinkThreshold('timing', 'varianceMajorPct', parseInt(e.target.value, 10) || 80)}
                />
              </div>
            </div>
            <div className="threshold-group">
              <h4>{t('sequence')}</h4>
              <div className="threshold-row">
                <label>Severity</label>
                <select
                  value={linkThresholdsForm.sequence?.severity ?? 'major'}
                  onChange={(e) => updateLinkThreshold('sequence', 'severity', e.target.value)}
                >
                  <option value="minor">{t('nomenclatures:options.minor')}</option>
                  <option value="major">{t('nomenclatures:options.major')}</option>
                  <option value="critical">{t('nomenclatures:options.critical')}</option>
                </select>
              </div>
              <div className="threshold-row">
                <label>Compliance impact</label>
                <input
                  type="number"
                  min="0"
                  value={linkThresholdsForm.sequence?.complianceImpact ?? 15}
                  onChange={(e) => updateLinkThreshold('sequence', 'complianceImpact', parseInt(e.target.value, 10) || 15)}
                />
              </div>
            </div>
            <div className="threshold-group">
              <h4>{t('risk')}</h4>
              <div className="threshold-row">
                <label>RPN threshold</label>
                <input
                  type="number"
                  min="0"
                  value={linkThresholdsForm.risk?.rpnThreshold ?? 500}
                  onChange={(e) => updateLinkThreshold('risk', 'rpnThreshold', parseInt(e.target.value, 10) || 500)}
                />
              </div>
              <div className="threshold-row">
                <label>Severity</label>
                <select
                  value={linkThresholdsForm.risk?.severity ?? 'critical'}
                  onChange={(e) => updateLinkThreshold('risk', 'severity', e.target.value)}
                >
                  <option value="minor">{t('nomenclatures:options.minor')}</option>
                  <option value="major">{t('nomenclatures:options.major')}</option>
                  <option value="critical">{t('nomenclatures:options.critical')}</option>
                </select>
              </div>
            </div>
          </div>
          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowLinkThresholdsModalForSrId(null)}
            >
              {t('cancel')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={savingLinkThresholds}
              onClick={handleSaveLinkThresholds}
            >
              {savingLinkThresholds ? t('saving') : t('save')}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default StandardDetail;
